import React from 'react';
import WrappedModal from 'components/modal/WrappedModal';
import { CustomerGroupFormFields } from './CustomerGroupFormFields';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Message } from 'semantic';
import { Formik, FormikState } from 'formik';
import { CustomerGroup } from 'subdomains/cpo/types/charging-policy';
import { customerGroupSchema } from 'subdomains/cpo/screens/PoliciesAndGroups/validation';
import { request } from 'utils/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Props = {
  trigger: React.ReactNode;
  afterSubmit: (id?: string) => void;
  mode: 'create' | 'edit';
  values?: FormikState<CustomerGroup>['values'];
};

export function EditCustomerGroupModal({
  values,
  trigger,
  mode,
  afterSubmit,
}: Props) {
  const [isOpen, setIsOpen] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: (values: CustomerGroup) => {
      if (mode === 'edit') {
        return request({
          method: 'PUT',
          path: `/1/customer-groups/${values?.id}`,
          body: values,
        });
      }
      return request<{ data: { id: string } }>({
        method: 'POST',
        path: '/1/customer-groups',
        body: values,
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customer-groups'] });
      afterSubmit(res.data.id);
      setIsOpen(false);
    },
    onError: () => {
      setError(
        t(
          'customerGroup.errorSubmittingForm',
          'Something went wrong while submitting the form. Please try again, or contact us if the error persists.'
        )
      );
    },
  });

  const onSubmit = async (values: CustomerGroup) => {
    setError(null);
    mutation.mutate(values);
  };

  return (
    <WrappedModal isOpen={isOpen} setIsOpen={setIsOpen} trigger={trigger}>
      <Formik
        initialValues={values || ({ filters: [{}] } as CustomerGroup)}
        validationSchema={customerGroupSchema}
        onReset={() => setIsOpen(false)}
        onSubmit={onSubmit}>
        {({ handleSubmit, handleReset, values }) => (
          <>
            <Modal.Header>
              {mode === 'create'
                ? t(
                    'customerGroup.createCustomerGroupTitle',
                    'New Customer Group'
                  )
                : t(
                    'customerGroup.editCustomerGroupTitle',
                    'Edit Customer Group'
                  )}
            </Modal.Header>
            <Modal.Content>
              <Form>
                <CustomerGroupFormFields values={values} />
              </Form>
              {error && <Message error>{error}</Message>}
            </Modal.Content>
            <Modal.Actions>
              <Button
                content={t(
                  'customerGroup.createButton',
                  'Create Customer Group'
                )}
                as="button"
                style={{ marginLeft: '0' }}
                onClick={handleSubmit}
                loading={mutation.isLoading}
              />
              <Button
                content={t('customerGroup.cancelButton', 'Cancel')}
                as="button"
                basic
                onClick={handleReset}
              />
            </Modal.Actions>
          </>
        )}
      </Formik>
    </WrappedModal>
  );
}
